import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clock, CheckCircle2, ClipboardCheck, Lock, Paperclip, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { errMsg } from '@/components/kit/data';

/**
 * A lesson for a student. Time counts while the lesson is on screen and the student is active; once the teacher's
 * minimum reading time is reached they can mark it done, which opens the lesson quiz.
 */

interface Quiz { exam_id: number; title: string; status: string; duration_minutes: number; attempts: number; max_attempts: number; unlocked: boolean; score: number | null; taken: boolean }
interface Lesson { id: number; subject: string | null; teacher: string | null; week: number | null; topic: string; objectives: string | null; content: string | null; video_url: string | null; due_at: string | null;
  resources: ({ title?: string; name?: string; url: string; kind?: string } | string)[] | null; min_reading_minutes: number | null; seconds_spent: number; can_complete: boolean; completed_at: string | null; quiz: Quiz | null; state: string }

const BEAT = 30;
const ytId = (u?: string | null) => u?.match(/(?:v=|youtu\.be\/|\/embed\/|\/live\/)([A-Za-z0-9_-]{11})/)?.[1];
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function LessonDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['student-lesson', id], queryFn: () => api.get(`/student/lessons/${id}`).then((r) => r.data?.data as Lesson), refetchOnWindowFocus: false });
  const [l, setL] = useState<Lesson | null>(null);
  const [local, setLocal] = useState(0);            // seconds read on this page not yet sent (for display)
  const unsent = useRef(0);
  const [err, setErr] = useState('');
  const lastActive = useRef(Date.now());
  useEffect(() => { if (q.data) setL(q.data); }, [q.data]);

  const send = async () => {
    const sec = Math.min(120, unsent.current);
    if (sec < 1) return;
    unsent.current -= sec;
    try {
      const d = (await api.post(`/student/lessons/${id}/heartbeat`, { seconds: sec })).data?.data;
      setL((x) => (x ? { ...x, ...d } : x));
    } catch { unsent.current += sec; }
    setLocal(unsent.current);
  };

  // count only while the tab is visible and the student did something in the last 3 minutes; report every 30 s
  useEffect(() => {
    if (!l || l.completed_at) return;
    const evs = ['scroll', 'mousemove', 'keydown', 'touchstart', 'click'] as const;
    const touch = () => { lastActive.current = Date.now(); };
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const tick = setInterval(() => { if (document.visibilityState === 'visible' && Date.now() - lastActive.current < 180_000) { unsent.current += 1; setLocal(unsent.current); } }, 1000);
    const beat = setInterval(() => { if (unsent.current >= 5) send(); }, BEAT * 1000);
    const hide = () => { if (document.visibilityState === 'hidden') send(); };
    document.addEventListener('visibilitychange', hide);
    return () => { clearInterval(tick); clearInterval(beat); document.removeEventListener('visibilitychange', hide); evs.forEach((e) => window.removeEventListener(e, touch)); send(); };
  }, [l?.id, l?.completed_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const complete = useMutation({
    mutationFn: async () => { await send(); return api.post(`/student/lessons/${id}/complete`).then((r) => r.data?.data); },
    onSuccess: (d) => { setL((x) => (x ? { ...x, ...d } : x)); qc.invalidateQueries({ queryKey: ['student-lessons'] }); setErr(''); },
    onError: (e) => setErr(errMsg(e)),
  });

  if (!l) return <p className="p-8 text-center text-sm text-slate-500">{q.isError ? errMsg(q.error) : 'Loading…'}</p>;
  const need = (l.min_reading_minutes ?? 0) * 60;
  const spent = l.seconds_spent + (l.completed_at ? 0 : local);
  const pct = need ? Math.min(100, (spent / need) * 100) : 100;
  const reached = spent >= need;
  const yt = ytId(l.video_url);

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-28">
      <div className="flex items-start gap-2">
        <Link to="/student/learn" className="p-2 -ml-2 rounded-lg hover:bg-slate-100"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="min-w-0"><p className="text-xs font-semibold text-slate-500">{l.subject}{l.week ? ` · Week ${l.week}` : ''}{l.teacher ? ` · ${l.teacher}` : ''}</p><h1 className="text-2xl font-extrabold text-slate-900 leading-tight">{l.topic}</h1>
          {l.due_at && <p className="text-xs text-amber-700 mt-0.5">Finish by {new Date(l.due_at).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}</div>
      </div>

      {l.objectives && <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4"><p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1">In this lesson</p><p className="text-sm text-blue-950 whitespace-pre-wrap">{l.objectives}</p></div>}
      {yt && <div className="aspect-video rounded-2xl overflow-hidden bg-black"><iframe title="Lesson video" src={`https://www.youtube.com/embed/${yt}`} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen /></div>}
      {l.video_url && !yt && <a href={l.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540]">Watch the lesson video<ExternalLink className="w-4 h-4" /></a>}

      <article className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 text-[15px] leading-7 text-slate-800 space-y-4">
        {(l.content || 'Your teacher has not added notes to this lesson yet — check the files below.').split(/\n{2,}/).map((p, i) => <p key={i} className="whitespace-pre-wrap">{p}</p>)}
      </article>

      {!!l.resources?.length && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="font-bold text-sm mb-2">Files & links</p>
          <ul className="space-y-1.5">{l.resources.map((r, i) => { const url = typeof r === 'string' ? r : r.url; const name = typeof r === 'string' ? r : r.title ?? r.name ?? r.url;
            return <li key={i}><a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm hover:bg-slate-100"><Paperclip className="w-4 h-4 text-slate-400" /><span className="flex-1 truncate">{name}</span><ExternalLink className="w-3.5 h-3.5 text-slate-400" /></a></li>; })}</ul>
        </div>
      )}

      {err && <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2">{err}</p>}

      {/* sticky progress / actions */}
      <div className="fixed bottom-0 inset-x-0 md:left-64 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2.5">
          <div className="flex-1 min-w-0">
            {l.completed_at ? <p className="text-sm font-semibold text-green-700 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Lesson done{l.quiz ? (l.quiz.taken ? ` · quiz taken${l.quiz.score != null ? ` (${l.quiz.score})` : ''}` : ' · now take the quiz') : ''}</p> : (
              <>
                <p className="text-xs text-slate-600 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{need ? (reached ? 'Reading time reached — you can mark it done.' : `Read for ${mmss(Math.max(0, need - spent))} more`) : 'Read the lesson, then mark it done.'}</p>
                {need > 0 && <div className="h-1.5 mt-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full transition-all ${reached ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} /></div>}
              </>
            )}
          </div>
          <div className="flex gap-2">
            {!l.completed_at && <button disabled={!reached || complete.isPending} onClick={() => complete.mutate()} className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{complete.isPending ? 'Saving…' : 'Mark as done'}</button>}
            {l.quiz && (
              <button disabled={!l.completed_at || (l.quiz.taken && l.quiz.attempts >= l.quiz.max_attempts)} onClick={() => nav(`/student/tests?exam=${l.quiz!.exam_id}`)} className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:bg-slate-200 disabled:text-slate-500 inline-flex items-center justify-center gap-1.5">
                {l.completed_at ? <ClipboardCheck className="w-4 h-4" /> : <Lock className="w-4 h-4" />}{l.quiz.taken ? (l.quiz.attempts < l.quiz.max_attempts ? 'Retake quiz' : 'Quiz taken') : 'Take the quiz'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
